/**
 * @fileoverview Hooks for single-product statistics (aggregate + time series).
 * @module features/home/hooks/useProductStatistics
 */

import { useMemo } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  statisticsService,
  type TimeAggregation,
  type UnifiedStatisticsFilterDTO,
  type UnifiedStatisticsResponseDTO,
  type StatisticsMetricsDTO,
  type StatisticsDimensionGroup,
} from '@/lib/statisticsService'
import { useFilter } from '@/shared/hooks/useFilter'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { useDashboardData } from '@/shared/hooks/dashboard/useDashboardData'
import { prefixWithOrg, queryKeys } from '@/shared/utils/queryKeys'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { computePreviousWindow } from '@/features/home/utils/metricDeltas'
import type { AggregationType } from '@/shared/context/FilterContext'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mapAggregation(a: AggregationType): TimeAggregation {
  const map: Record<AggregationType, TimeAggregation> = {
    Daily: 'DAILY',
    Weekly: 'WEEKLY',
    Monthly: 'MONTHLY',
    Quarterly: 'QUARTERLY',
    Yearly: 'YEARLY',
  }
  return map[a] ?? 'MONTHLY'
}

/**
 * Build the filter DTO + time aggregation from current FilterContext values,
 * scoped to a specific set of member SKUs.
 */
function useProductFilter(memberSkus: string[]): {
  filter: UnifiedStatisticsFilterDTO
  timeAggregation: TimeAggregation
} {
  const { startDate, endDate, aggregation } = useFilter()

  return useMemo(() => {
    const f: UnifiedStatisticsFilterDTO = { skus: memberSkus }
    if (startDate) f.startDate = toDateOnlyStringLocal(startDate)
    if (endDate) f.endDate = toDateOnlyStringLocal(endDate)
    return { filter: f, timeAggregation: mapAggregation(aggregation) }
  }, [startDate, endDate, aggregation, memberSkus])
}

// ---------------------------------------------------------------------------
// Variant metrics derived from aggregate query
// ---------------------------------------------------------------------------

export interface VariantMetrics {
  sku: string
  label: string
  imageUrl: string | null
  sales: number
  quantity: number
  /** Units belonging to fully-cancelled orders (subset of quantity). */
  cancelledQuantity: number
  advertising: number
  platformBreakdown: Record<string, number>
  cogs: number
  directCosts: number
  cm2: number
  roas: number
  cm2Margin: number
  orderCount: number
  cancellationCount: number
  returnRate: number
  potentialRevenueLoss: number
}

export interface ProductAggregateResult {
  /** Totals across all variants */
  totals: StatisticsMetricsDTO
  /** Per-variant breakdown */
  variants: VariantMetrics[]
  /** Dimension group metadata for the product */
  dimensionGroup: StatisticsDimensionGroup | null
  /** Raw response for advanced usage */
  raw: UnifiedStatisticsResponseDTO | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
}

/**
 * Fetches aggregated product statistics with per-variant breakdown.
 *
 * @param memberSkus - Variant SKU keys belonging to the product
 * @returns Aggregated totals + per-variant metrics
 * @example
 * ```ts
 * const { totals, variants, isLoading } = useProductAggregate(['sku-a', 'sku-b'])
 * ```
 */
export function useProductAggregate(memberSkus: string[]): ProductAggregateResult {
  const { activeOrgId } = useActiveOrg()
  const { filter, timeAggregation } = useProductFilter(memberSkus)
  const { variantsQuery } = useDashboardData()

  // Build SKU → variant metadata lookup from /api/variants data
  const variantLookup = useMemo(() => {
    const map = new Map<string, { name: string; imageUrl: string | null }>()
    for (const v of variantsQuery.data ?? []) {
      if (v.variantSkuKey) map.set(v.variantSkuKey, { name: v.name, imageUrl: v.imageUrl })
    }
    return map
  }, [variantsQuery.data])

  const variantFilter = { ...filter, group: 'VARIANT' as const }

  const query = useQuery<UnifiedStatisticsResponseDTO>({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.bothStatistics(variantFilter, ['PRODUCT'], timeAggregation)
        )
      : queryKeys.bothStatistics(variantFilter, ['PRODUCT'], timeAggregation),
    queryFn: () =>
      statisticsService.getBothStatistics(variantFilter, ['PRODUCT'], timeAggregation),
    enabled: Boolean(activeOrgId && memberSkus.length > 0 && filter.startDate && filter.endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { totals, variants, dimensionGroup } = useMemo(() => {
    const data = query.data
    if (!data?.data?.length) {
      return { totals: {} as StatisticsMetricsDTO, variants: [], dimensionGroup: null }
    }

    const labels = data.metadata.labels?.product ?? {}
    const groups = data.metadata.dimensionGroups?.product ?? {}
    const firstGroupKey = Object.keys(groups)[0]
    const group = firstGroupKey ? groups[firstGroupKey] : null

    // Restrict the per-variant breakdown to THIS product's own variants. The
    // collection-spend allocator injects synthetic ad-spend rows for every member
    // of a shared tag — plus a negative __unmapped__ cancellation row — at read
    // time; on a product-scoped query those would otherwise surface as foreign
    // variant rows. Totals still accumulate over all points below, so the injected
    // rows stay conserved (they net to zero) and the ad-spend totals are unaffected.
    const memberSet = new Set(memberSkus)

    // Aggregate totals across all data points
    const agg: StatisticsMetricsDTO = {
      sales: 0,
      quantity: 0,
      cancelledQuantity: 0,
      advertising: 0,
      platformBreakdown: {},
      cogs: 0,
      directCosts: 0,
      cm2: 0,
      orderCount: 0,
      cancellationCount: 0,
      returnRate: 0,
      potentialRevenueLoss: 0,
    }

    const variantList: VariantMetrics[] = []

    for (const point of data.data) {
      const sku = String(point.dimensions['product'] ?? '')
      const m = point.metrics

      // Accumulate totals
      agg.sales = (agg.sales ?? 0) + (m.sales ?? 0)
      agg.quantity = (agg.quantity ?? 0) + (m.quantity ?? 0)
      agg.cancelledQuantity = (agg.cancelledQuantity ?? 0) + (m.cancelledQuantity ?? 0)
      agg.advertising = (agg.advertising ?? 0) + (m.advertising ?? 0)
      agg.cogs = (agg.cogs ?? 0) + (m.cogs ?? 0)
      agg.directCosts = (agg.directCosts ?? 0) + (m.directCosts ?? 0)
      agg.cm2 = (agg.cm2 ?? 0) + (m.cm2 ?? 0)
      agg.orderCount = (agg.orderCount ?? 0) + (m.orderCount ?? 0)
      agg.cancellationCount = (agg.cancellationCount ?? 0) + (m.cancellationCount ?? 0)
      agg.potentialRevenueLoss = (agg.potentialRevenueLoss ?? 0) + (m.potentialRevenueLoss ?? 0)

      // Merge platform breakdown
      if (m.platformBreakdown) {
        for (const [platform, amount] of Object.entries(m.platformBreakdown)) {
          agg.platformBreakdown![platform] = (agg.platformBreakdown![platform] ?? 0) + amount
        }
      }

      const sales = m.sales ?? 0
      const advertising = m.advertising ?? 0
      const cm2Val = m.cm2 ?? 0

      // Only this product's own variants belong in the breakdown; allocator-injected
      // rows for foreign tag members (and __unmapped__) are excluded here.
      if (memberSet.has(sku)) {
        const variant = variantLookup.get(sku)

        variantList.push({
          sku,
          label: variant?.name ?? group?.memberLabels?.[sku] ?? labels[sku] ?? sku,
          imageUrl: variant?.imageUrl ?? null,
          sales,
          quantity: m.quantity ?? 0,
          cancelledQuantity: m.cancelledQuantity ?? 0,
          advertising,
          platformBreakdown: m.platformBreakdown ?? {},
          cogs: m.cogs ?? 0,
          directCosts: m.directCosts ?? 0,
          cm2: cm2Val,
          roas: sales > 0 && advertising > 0 ? sales / advertising : 0,
          cm2Margin: sales > 0 ? cm2Val / sales : 0,
          orderCount: m.orderCount ?? 0,
          cancellationCount: m.cancellationCount ?? 0,
          returnRate: m.returnRate ?? 0,
          potentialRevenueLoss: m.potentialRevenueLoss ?? 0,
        })
      }
    }

    // Prefer the authoritative distinct-order count from backend metadata.
    // Summing per-row orderCount inflates the total under groupBy=PRODUCT
    // (one order with N products fans across N rows).
    const totalOrderCountFromMeta = data.metadata?.totalOrderCount
    if (typeof totalOrderCountFromMeta === 'number') {
      agg.orderCount = totalOrderCountFromMeta
    }

    // Compute derived totals
    const totalSales = agg.sales ?? 0
    const totalAd = agg.advertising ?? 0
    const totalCm2 = agg.cm2 ?? 0
    agg.roas = totalSales > 0 && totalAd > 0 ? totalSales / totalAd : 0
    agg.cm2Margin = totalSales > 0 ? totalCm2 / totalSales : 0
    const totalOrders = agg.orderCount ?? 0
    const totalCancellations = agg.cancellationCount ?? 0
    agg.returnRate = totalOrders > 0 ? totalCancellations / totalOrders : 0

    // Sort variants by revenue descending
    variantList.sort((a, b) => b.sales - a.sales)

    return { totals: agg, variants: variantList, dimensionGroup: group }
  }, [query.data, variantLookup, memberSkus])

  return {
    totals,
    variants,
    dimensionGroup: dimensionGroup ?? null,
    raw: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}

// ---------------------------------------------------------------------------
// Country breakdown
// ---------------------------------------------------------------------------

export interface CountryMetrics {
  code: string
  country: string
  sales: number
}

export interface ProductByCountryResult {
  countries: CountryMetrics[]
  isLoading: boolean
}

/**
 * Fetches revenue breakdown by country for a product.
 */
export function useProductByCountry(memberSkus: string[]): ProductByCountryResult {
  const { activeOrgId } = useActiveOrg()
  const { filter, timeAggregation } = useProductFilter(memberSkus)

  const query = useQuery<UnifiedStatisticsResponseDTO>({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.bothStatistics(filter, ['COUNTRY'], timeAggregation)
        )
      : queryKeys.bothStatistics(filter, ['COUNTRY'], timeAggregation),
    queryFn: () =>
      statisticsService.getBothStatistics(filter, ['COUNTRY'], timeAggregation),
    enabled: Boolean(activeOrgId && memberSkus.length > 0 && filter.startDate && filter.endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const countries = useMemo<CountryMetrics[]>(() => {
    const points = query.data?.data ?? []
    const labels = query.data?.metadata.labels?.country ?? {}
    return points
      .map((point) => {
        const code = String(point.dimensions['country'] ?? '')
        return {
          code,
          country: labels[code] ?? code,
          sales: point.metrics.sales ?? 0,
        }
      })
      .filter((c) => c.sales > 0)
      .sort((a, b) => b.sales - a.sales)
  }, [query.data])

  return { countries, isLoading: query.isLoading }
}

// ---------------------------------------------------------------------------
// Time series data
// ---------------------------------------------------------------------------

export interface TimeSeriesPoint {
  period: string
  sales: number
  cm2: number
  advertising: number
  quantity: number
  orderCount: number
  cancellationCount: number
}

export interface ProductTimeSeriesResult {
  data: TimeSeriesPoint[]
  raw: UnifiedStatisticsResponseDTO | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
}

/**
 * Fetches time-bucketed statistics for a product.
 *
 * @param memberSkus - Variant SKU keys belonging to the product
 * @returns Time series data points sorted chronologically
 * @example
 * ```ts
 * const { data, isLoading } = useProductTimeSeries(['sku-a', 'sku-b'])
 * ```
 */
export function useProductTimeSeries(memberSkus: string[]): ProductTimeSeriesResult {
  const { activeOrgId } = useActiveOrg()
  const { filter, timeAggregation } = useProductFilter(memberSkus)

  const query = useQuery<UnifiedStatisticsResponseDTO>({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.bothStatistics(filter, ['TIME'], timeAggregation)
        )
      : queryKeys.bothStatistics(filter, ['TIME'], timeAggregation),
    queryFn: () =>
      statisticsService.getBothStatistics(filter, ['TIME'], timeAggregation),
    enabled: Boolean(activeOrgId && memberSkus.length > 0 && filter.startDate && filter.endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const data = useMemo<TimeSeriesPoint[]>(() => {
    const points = query.data?.data ?? []
    return points
      .map((point) => ({
        period: String(point.dimensions['timePeriod'] ?? ''),
        sales: point.metrics.sales ?? 0,
        cm2: point.metrics.cm2 ?? 0,
        advertising: point.metrics.advertising ?? 0,
        quantity: point.metrics.quantity ?? 0,
        orderCount: point.metrics.orderCount ?? 0,
        cancellationCount: point.metrics.cancellationCount ?? 0,
      }))
      .sort((a, b) => a.period.localeCompare(b.period))
  }, [query.data])

  return {
    data,
    raw: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}

// ---------------------------------------------------------------------------
// Previous-period totals (for hero deltas)
// ---------------------------------------------------------------------------

export interface PreviousPeriodTotals {
  sales: number
  advertising: number
  cm2: number
  roas: number
}

export interface ProductPreviousTotalsResult {
  /** undefined when the previous window has no data - deltas are hidden then */
  totals: PreviousPeriodTotals | undefined
  isLoading: boolean
}

/**
 * Fetches aggregate totals for the equal-length window immediately preceding
 * the current filter window. Drives the period deltas in the product hero.
 *
 * @param memberSkus - Variant SKU keys belonging to the product
 * @returns Previous-window totals, or undefined when there is no baseline
 * @example
 * ```ts
 * const { totals } = useProductPreviousTotals(['sku-a', 'sku-b'])
 * ```
 */
export function useProductPreviousTotals(memberSkus: string[]): ProductPreviousTotalsResult {
  const { activeOrgId } = useActiveOrg()
  const { startDate, endDate, aggregation } = useFilter()
  const timeAggregation = mapAggregation(aggregation)

  const filter = useMemo<UnifiedStatisticsFilterDTO | null>(() => {
    if (!startDate || !endDate) return null
    const { prevStart, prevEnd } = computePreviousWindow(startDate, endDate)
    return {
      skus: memberSkus,
      group: 'VARIANT' as const,
      startDate: toDateOnlyStringLocal(prevStart),
      endDate: toDateOnlyStringLocal(prevEnd),
    }
  }, [startDate, endDate, memberSkus])

  const effectiveFilter = filter ?? { skus: memberSkus }

  const query = useQuery<UnifiedStatisticsResponseDTO>({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.bothStatistics(effectiveFilter, ['PRODUCT'], timeAggregation)
        )
      : queryKeys.bothStatistics(effectiveFilter, ['PRODUCT'], timeAggregation),
    queryFn: () =>
      statisticsService.getBothStatistics(effectiveFilter, ['PRODUCT'], timeAggregation),
    enabled: Boolean(activeOrgId && memberSkus.length > 0 && filter),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  // Sum over ALL points (incl. allocator-injected rows), mirroring how
  // useProductAggregate computes its totals - keeps both windows comparable.
  const totals = useMemo<PreviousPeriodTotals | undefined>(() => {
    const points = query.data?.data
    if (!points?.length) return undefined
    let sales = 0
    let advertising = 0
    let cm2 = 0
    for (const point of points) {
      sales += point.metrics.sales ?? 0
      advertising += point.metrics.advertising ?? 0
      cm2 += point.metrics.cm2 ?? 0
    }
    return {
      sales,
      advertising,
      cm2,
      roas: sales > 0 && advertising > 0 ? sales / advertising : 0,
    }
  }, [query.data])

  return { totals, isLoading: query.isLoading }
}
